import { useMutation } from '@apollo/client'
import { FormattedMessage } from '@faceup/localization'
import { Alert, Button } from '@faceup/ui-base'
import { CaseStatusGroup } from '@faceup/utils'
import { type ComponentProps, useContext } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../../__generated__'
import { ChatPopupAlertType, PopupsContext } from './PopupsContext'
import { RelativeMessagesContext } from './RelativeMessagesContext'

const fragments = {
  ReportStatusAlert_case: graphql(`
    fragment ReportStatusAlert_case on CaseInterface {
      id
      status {
        id
        groupName
      }
      isHiddenFromStatistics
      isDemo
      channel {
        id
        sourceType
      }
      ... on CompanyReport {
        status {
          groupName
        }
      }
      organizationalUnit {
        id
        workingHours {
          id
          enabled
          outsideWorkingHoursMessage
        }
      }
      victim {
        id
      }
    }
  `),
}

const mutations = {
  ReopenCompanyReportMutation: graphql(`
    mutation ReopenCompanyReportMutation {
      reopenCompanyReport {
        case {
          id
          status {
            id
            groupName
          }
        }
      }
    }
  `),
}

type Props = {
  userId: string
  case: FragmentType<typeof fragments.ReportStatusAlert_case>
  isFirstMessage?: boolean
}

const ReportStatusAlert = ({ case: _case, userId, isFirstMessage }: Props) => {
  const aCase = getFragmentData(fragments.ReportStatusAlert_case, _case)
  const relativeMessages = useContext(RelativeMessagesContext)
  const { alertType, closeNotOpenReportAlert } = useContext(PopupsContext)
  const [reopenChat] = useMutation(mutations.ReopenCompanyReportMutation, {
    onCompleted: () => {
      closeNotOpenReportAlert()
    },
  })

  const userIsVictim = Boolean(aCase?.victim?.id) && userId === aCase?.victim?.id
  const isHiddenFromStatistics = aCase.isHiddenFromStatistics
  const isFollowUp = relativeMessages.type === 'Victim'

  if (alertType === ChatPopupAlertType.ReportStatus) {
    return (
      <div className='flex flex-col gap-5'>
        <AlertMessage
          data-test='report-status-alert-report-closed'
          message={
            <FormattedMessage
              message={
                isHiddenFromStatistics
                  ? relativeMessages.archivedMessagesPerChannel[aCase.channel.sourceType]
                  : relativeMessages.closedMessagesPerChannel[aCase.channel.sourceType]
              }
            />
          }
          statusGroupName={aCase.status.groupName}
        />
        {userIsVictim && (
          <div className='self-center'>
            <Button type='primary' onClick={() => reopenChat()}>
              {isFollowUp && <FormattedMessage message={relativeMessages.reopen} />}
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (alertType === ChatPopupAlertType.OutsideWorkingHours && userIsVictim && isFirstMessage) {
    return (
      <AlertMessage
        data-test='report-status-alert-outside-working-hours'
        message={aCase.organizationalUnit.workingHours?.outsideWorkingHoursMessage ?? ''}
        closable
        afterClose={() => closeNotOpenReportAlert()}
        statusGroupName={aCase.status.groupName}
      />
    )
  }

  return null
}

// Map the status group to an antd Alert type so background + border follow the status color.
const statusAlertType = (
  statusGroupName: CaseStatusGroup
): ComponentProps<typeof Alert>['type'] => {
  switch (statusGroupName) {
    case CaseStatusGroup.Closed:
      return 'info'
    case CaseStatusGroup.Open:
      return 'success'
    default:
      return 'warning'
  }
}

type AlertMessageProps = ComponentProps<typeof Alert> & {
  statusGroupName: CaseStatusGroup
}

const AlertMessage = ({ statusGroupName, ...props }: AlertMessageProps) => (
  <Alert type={statusAlertType(statusGroupName)} {...props} />
)

export default ReportStatusAlert
